/*
Origin Core 1.0

called from /snippets/scripts.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script normalizes internal URLs injected by native Nosto placements so they
respect the active Shopify storefront root (for example `/en-us/`). It patches
existing placement links on load and observes future Nosto DOM injections.

*/

function getStorefrontRoot() {
  const rawRoot = String(window.Shopify?.routes?.root || '/').trim();
  if (!rawRoot || rawRoot === '/') return '/';

  return `/${rawRoot.replace(/^\/+|\/+$/g, '')}/`;
}

function hasLocalePrefix(pathname) {
  return /^\/[a-z]{2}(?:-[a-z]{2})?(?:\/|$)/i.test(pathname);
}

function applyStorefrontRoot(urlObject) {
  if (!(urlObject instanceof URL)) return urlObject;
  if (urlObject.origin !== window.location.origin) return urlObject;

  const storefrontRoot = getStorefrontRoot();
  if (storefrontRoot === '/') return urlObject;
  if (urlObject.pathname.startsWith(storefrontRoot) || hasLocalePrefix(urlObject.pathname)) return urlObject;

  const rootedPath = `${storefrontRoot.replace(/\/$/, '')}${urlObject.pathname}`;
  return new URL(`${rootedPath}${urlObject.search}${urlObject.hash}`, window.location.origin);
}

function normalizeStorefrontUrl(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value || value.startsWith('#') || /^(mailto|tel|javascript):/i.test(value)) return value;

  if (/^shopify:\/\//i.test(value)) {
    const path = `/${value.replace(/^shopify:\/\//i, '').replace(/^\/+/, '')}`;

    try {
      return applyStorefrontRoot(new URL(path, window.location.origin)).toString();
    } catch (error) {
      return value;
    }
  }

  try {
    return applyStorefrontRoot(new URL(value, window.location.origin)).toString();
  } catch (error) {
    return value;
  }
}

function patchPlacementLink(link) {
  if (!(link instanceof HTMLAnchorElement)) return;
  if (!link.closest('.nosto_element')) return;

  const href = link.getAttribute('href');
  if (!href) return;

  const normalizedHref = normalizeStorefrontUrl(href);
  if (!normalizedHref || normalizedHref === href) return;

  link.setAttribute('href', normalizedHref);
}

function patchPlacementLinks(root) {
  if (!(root instanceof Element)) return;

  if (root instanceof HTMLAnchorElement) {
    patchPlacementLink(root);
    return;
  }

  // Unqualified `a[href]`, not `.nosto_element a[href]`: `root` is normally the placement
  // itself or a node Nosto injected inside it, so a descendant-of-`.nosto_element` selector
  // would match nothing. `patchPlacementLink` re-checks that ancestry per link anyway.
  root.querySelectorAll('a[href]').forEach(patchPlacementLink);
}

/*
 * Scoping note (INP)
 * ------------------
 * This used to observe `document.body` with `subtree: true`, which meant every DOM write
 * anywhere on the page — including the 24 per-card `innerHTML` writes a Nosto collection
 * filter/sort performs — had to be evaluated here, at a `querySelector` per added node. No
 * `.nosto_element` ever appears in the product grid, so that work was entirely wasted.
 *
 * Placement containers are server-rendered by `snippets/nosto-placement.liquid`, so each one
 * is observed individually instead. The single exception is the predictive-search trending
 * placement, which ships inside `<template data-open-search-products-template>`
 * (`snippets/predictive-search.liquid`) and is cloned into the results container on open —
 * and re-created on every query. `<predictive-search-component>` is therefore watched as a
 * host, so that placement is picked up whenever it reappears. That subtree is small and only
 * mutates while the search overlay is in use.
 */

/** Placements already wired up, so re-scans stay idempotent. @type {WeakSet<Element>} */
const observedPlacements = new WeakSet();

/** Hosts that can clone a placement into the DOM after load. */
const LATE_PLACEMENT_HOST_SELECTOR = 'predictive-search-component';

const placementObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes') {
      patchPlacementLink(mutation.target);
      return;
    }

    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;

      if (node instanceof HTMLAnchorElement) {
        patchPlacementLink(node);
        return;
      }

      patchPlacementLinks(node);
    });
  });
});

/**
 * Patches a placement's existing links and watches it for the content Nosto injects later.
 *
 * @param {Element} placement - A `.nosto_element` container.
 */
function observePlacement(placement) {
  if (!(placement instanceof Element) || observedPlacements.has(placement)) return;

  observedPlacements.add(placement);
  patchPlacementLinks(placement);

  placementObserver.observe(placement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['href'],
  });
}

/**
 * Finds and observes every placement within a root.
 *
 * @param {Document | Element} [root] - The subtree to scan.
 */
function observePlacementsWithin(root = document) {
  if (!(root instanceof Document) && !(root instanceof Element)) return;

  if (root instanceof Element && root.matches('.nosto_element')) {
    observePlacement(root);
    return;
  }

  root.querySelectorAll('.nosto_element').forEach(observePlacement);
}

const hostObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;

      observePlacementsWithin(node);
    });
  });
});

function observeNostoPlacements() {
  if (!(document.body instanceof HTMLElement)) return;

  observePlacementsWithin(document);

  document.querySelectorAll(LATE_PLACEMENT_HOST_SELECTOR).forEach((host) => {
    hostObserver.observe(host, { subtree: true, childList: true });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeNostoPlacements, { once: true });
} else {
  observeNostoPlacements();
}
